import { useState, useEffect, useRef, useCallback } from "react";
import { useGetAllStudentsQuery } from "../../../peoples/students/api/studentApi";
import { StudentResponseType } from "../../../peoples/students/types/studentResponse";
import {
  StudentAttendanceTypes,
  AttendanceApiResponse,
} from "../types/attendanceTypes";
import { API_CONFIG } from "../../../../core/config";
import {
  useGetAllAttendanceQuery,
  useCreateAttendanceMutation,
  useUpdateAttendanceMutation,
  useCreateBulkAttendanceMutation,
  useUpdateBulkAttendanceMutation,
} from "../api/attendanceApi";
import { showToast } from "../../../../core/utils/alert";
import { PaginationProps } from "../../../../core/data/interface";
import { useBatchTiming } from "../../../configuration/batch-timing/hooks/useBatchTiming";
import dayjs from "dayjs";
export const useAttendance = () => {
  const LAZY_PAGE_SIZE = 100;
  const currentDate = new Date().toISOString().split("T")[0];
  const [selectedStudentKeys, setSelectedStudentKeys] = useState<React.Key[]>(
    []
  );
  const [bulkStatus, setBulkStatus] = useState("Present");



  const getDefaultSunday = () => {
    const today = dayjs();

    // Sunday = 0
    if (today.day() === 0) {
      return today.format("YYYY-MM-DD");
    }

    return today.subtract(today.day(), "day").format("YYYY-MM-DD");
  };
  const [filterDate, setFilterDate] = useState(getDefaultSunday());

  const [students, setStudents] = useState<StudentAttendanceTypes[]>([]);
  const [allAttendanceRecords, setAllAttendanceRecords] = useState<
    AttendanceApiResponse[]
  >([]);
  const [filters, setFilters] = useState<any>(() => {
    try {
      const saved = sessionStorage.getItem("studentAttendanceFilters");
      if (!saved) return {};
      const parsed = JSON.parse(saved);
      // Strip any non-positive-integer values (e.g. stale "all" strings) before
      // they reach the API. Only keep keys whose value is a valid positive number.
      return Object.fromEntries(
        Object.entries(parsed).filter(([, v]) => {
          if (v === undefined || v === null || v === "") return false;
          const n = Number(v);
          return !isNaN(n) && n > 0;
        })
      );
    } catch {
      return {};
    }
  });
  const [tempFilters, setTempFilters] = useState<any>(() => {
    try {
      const saved = sessionStorage.getItem("studentAttendanceFilters");
      if (!saved) return {};
      const parsed = JSON.parse(saved);
      return Object.fromEntries(
        Object.entries(parsed).filter(([, v]) => {
          if (v === undefined || v === null || v === "") return false;
          const n = Number(v);
          return !isNaN(n) && n > 0;
        })
      );
    } catch {
      return {};
    }
  });
  const [selectedCourseStages, setSelectedCourseStages] = useState<any[]>([]);
  const [sortConfig, setSortConfig] = useState<any>({});

  // Strip out any falsy/null/undefined/NaN values so they are never sent to the API
  const sanitizeFilters = (raw: any) => {
    return Object.fromEntries(
      Object.entries(raw).filter(
        ([, v]) => v !== undefined && v !== null && v !== "" && !Number.isNaN(v)
      )
    );
  };

  useEffect(() => {
    sessionStorage.setItem("studentAttendanceFilters", JSON.stringify(filters));
  }, [filters]);

  // Pagination & Lazy Loading States
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [search, setSearch] = useState("");

  const [isAllMode, setIsAllMode] = useState(false);
  const [lazyPage, setLazyPage] = useState(1);
  const [accumulatedStudents, setAccumulatedStudents] = useState<StudentAttendanceTypes[]>([]);
  const [lazyHasMore, setLazyHasMore] = useState(true);

  const lastProcessedPage = useRef(0);
  const pageRequestedRef = useRef(false);

  const { data: studentData, isLoading, isFetching } = useGetAllStudentsQuery({
    page: isAllMode ? lazyPage : pageNumber,
    size: isAllMode ? LAZY_PAGE_SIZE : recordsPerPage,
    search: search,
    status: "active",
    ...filters,
    ...sortConfig,
  });

  useEffect(() => {
    // Reset accumulation when search, filters, or mode changes
    setAccumulatedStudents([]);
    setLazyPage(1);
    setLazyHasMore(true);
    lastProcessedPage.current = 0;
    pageRequestedRef.current = false;
  }, [search, filters, sortConfig, isAllMode]);

  const { data: initialAttendanceData } = useGetAllAttendanceQuery({
    date: filterDate,
    page: 1,
    size: 100,
  });

  const { batchTimings } = useBatchTiming();

  useEffect(() => {
    const fetchAllAttendance = async () => {
      if (!initialAttendanceData?.data) return;

      const totalPages = Math.ceil(
        (initialAttendanceData.data.totalItems || 0) / 100
      );
      let allRecords: AttendanceApiResponse[] = [];

      try {
        if (initialAttendanceData.data.items) {
          allRecords = [...initialAttendanceData.data.items];
        }

        for (let page = 2; page <= totalPages; page++) {
          const response = await fetch(
            `${API_CONFIG.BASE_URL}student-attendance/get-all?date=${filterDate}&page=${page}&size=100`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            }
          );
          const data = await response.json();
          if (data.success && data.data.items) {
            allRecords = [...allRecords, ...data.data.items];
          }
        }

        setAllAttendanceRecords(allRecords);
      } catch (error) {
        console.error("Error fetching attendance:", error);
      }
    };

    fetchAllAttendance();
  }, [filterDate, initialAttendanceData?.data]);

  useEffect(() => {
    if (studentData?.data) {
      const studentItems = studentData.data.items || [];

      const updatedStudents: StudentAttendanceTypes[] = studentItems
        .filter((student) => student.status !== "pending")
        .map((student: StudentResponseType) => {
          const existingAttendance = allAttendanceRecords.find(
            (a) => a.student_id === student.id && a.date === filterDate
          );

          const photoUrl = student.docs.photo
            ? `${API_CONFIG.BASE_URL}students/get-thumbnail/${student.docs.photo
            }?v=${Date.now()}`
            : student.profile.gender?.toLowerCase() === "female"
              ? "assets/img/profiles/avatar-03.jpg"
              : "assets/img/profiles/avatar-02.jpg";

          let attendanceStatus = "";
          if (existingAttendance) {
            attendanceStatus =
              existingAttendance.attendance_status.charAt(0).toUpperCase() +
              existingAttendance.attendance_status.slice(1).toLowerCase();
          }

          const levelName = student.course?.stage?.name || "-";

          return {
            id: existingAttendance?.id || student.id,
            studentId: student.profile.studentid || "",
            name: student.name || "",
            course: student.course?.name || "",
            level: levelName,
            attendanceDate: filterDate,
            attendanceStatus,
            week: student.current_week,
            student: {
              id: student.id,
              name: student.name,
              status: student.status,
              center: student.center
                ? {
                  id: student.center.id,
                  name: student.center.name,
                  code: student.center.code,
                  zone: student.center.zone,
                  district: student.center.district,
                  status: student.center.status,
                }
                : undefined,
            },
            img: photoUrl,
            gender: student.profile.gender || "male",
            key: student.id,
          };
        });

      if (isAllMode) {
        if (isFetching) return;
        if (lazyPage === lastProcessedPage.current) return;

        setLazyHasMore(updatedStudents.length === LAZY_PAGE_SIZE);
        lastProcessedPage.current = lazyPage;
        pageRequestedRef.current = false;

        setAccumulatedStudents(prev => {
          if (lazyPage === 1) return updatedStudents;
          const newUnique = updatedStudents.filter(
            newS => !prev.some(p => p.key === newS.key)
          );
          return [...prev, ...newUnique];
        });
      } else {
        setStudents(updatedStudents);
      }
    }
  }, [studentData, allAttendanceRecords, filterDate, batchTimings, isAllMode, lazyPage, isFetching]);

  const loadNextLazyPage = useCallback(() => {
    if (!isAllMode || !lazyHasMore) return;
    if (pageRequestedRef.current) return;
    pageRequestedRef.current = true;
    setLazyPage(prev => prev + 1);
  }, [isAllMode, lazyHasMore]);

  const resetLazyState = useCallback(() => {
    setAccumulatedStudents([]);
    setLazyPage(1);
    lastProcessedPage.current = 0;
    pageRequestedRef.current = false;
    setLazyHasMore(true);
  }, []);

  const handleSearchChange = (text: string) => {
    setSearch(text);
    setPageNumber(1);
    if (isAllMode) resetLazyState();
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    if (pageSize !== undefined) {
      const total = studentData?.data?.totalItems || 0;
      const isSelectingAll = total > 0 && pageSize >= total;

      if (isSelectingAll) {
        setIsAllMode(true);
        resetLazyState();
        setRecordsPerPage(pageSize);
        return;
      }

      // Switching back from All → normal pagination
      if (isAllMode) {
        setIsAllMode(false);
        resetLazyState();
      }
      setRecordsPerPage(pageSize);
    }
    setPageNumber(page);
  };

  const [createAttendance] = useCreateAttendanceMutation();
  const [updateAttendance] = useUpdateAttendanceMutation();
  const [createBulkAttendance, { isLoading: isCreatingBulk }] = useCreateBulkAttendanceMutation();
  const [updateBulkAttendance, { isLoading: isUpdatingBulk }] = useUpdateBulkAttendanceMutation();

  const isBulkUpdating = isCreatingBulk || isUpdatingBulk;

  const handleOptionChange = useCallback(async (
    studentKey: React.Key,
    newStatus: string
  ) => {
    try {
      const currentStudents = isAllMode ? accumulatedStudents : students;
      const student = currentStudents.find((s) => s.key === studentKey);
      if (!student) return;

      const existingAttendance = allAttendanceRecords.find(
        (a) => a.student_id === Number(studentKey) && a.date === filterDate
      );

      if (filterDate < currentDate && existingAttendance?.attendance_status) {
        showToast({
          title: "Error",
          text: "Cannot update attendance for past dates.",
          icon: "error",
          timer: 5000,
        });
        return;
      }

      if (existingAttendance) {
        await updateAttendance({
          id: existingAttendance.id,
          student_id: Number(studentKey),
          attendance_status: newStatus.toLowerCase(),
          date: filterDate,
        }).unwrap();

        showToast({
          title: "Success",
          text: "Attendance updated successfully",
          icon: "success",
          timer: 5000,
        });
      } else {
        await createAttendance({
          student_id: Number(studentKey),
          attendance_status: newStatus.toLowerCase(),
          date: filterDate,
        }).unwrap();

        showToast({
          title: "Success",
          text: "Attendance marked successfully",
          icon: "success",
          timer: 5000,
        });
      }

      setStudents((prevStudents) =>
        prevStudents.map((s) => {
          if (s.key === studentKey) {
            return {
              ...s,
              attendanceStatus:
                newStatus.charAt(0).toUpperCase() +
                newStatus.slice(1).toLowerCase(),
            };
          }
          return s;
        })
      );
      setAccumulatedStudents((prevStudents) =>
        prevStudents.map((s) => {
          if (s.key === studentKey) {
            return {
              ...s,
              attendanceStatus:
                newStatus.charAt(0).toUpperCase() +
                newStatus.slice(1).toLowerCase(),
            };
          }
          return s;
        })
      );
    } catch (error) {
      console.error("Failed to update attendance:", error);
      showToast({
        title: "Error",
        text: "Failed to update attendance. Please try again.",
        icon: "error",
        timer: 5000,
      });
    }
  }, [students, allAttendanceRecords, filterDate, currentDate, updateAttendance, createAttendance]);

  const handleBulkStatusChange = async () => {
    if (selectedStudentKeys.length === 0 || !bulkStatus) return;

    try {
      const currentStudents = isAllMode ? accumulatedStudents : students;
      const selectedStudents = currentStudents.filter((student) =>
        selectedStudentKeys.includes(student.key)
      );

      const hasPastDateAttendance = selectedStudents.some((student) => {
        const existingAttendance = allAttendanceRecords.find(
          (a) => a.student_id === student.key && a.date === filterDate
        );
        return (
          filterDate < currentDate && existingAttendance?.attendance_status
        );
      });

      if (hasPastDateAttendance) {
        showToast({
          title: "Error",
          text: "Cannot update attendance for past dates.",
          icon: "error",
          timer: 5000,
        });
        return;
      }

      const studentsToUpdate: Array<{
        id: number;
        student_id: number;
        attendance_status: string;
        date: string;
      }> = [];

      const studentsToCreate = selectedStudents.filter((student) => {
        const existingAttendance = allAttendanceRecords.find(
          (a) => a.student_id === student.key && a.date === filterDate
        );
        return !existingAttendance;
      });

      selectedStudents.forEach((student) => {
        const existingAttendance = allAttendanceRecords.find(
          (a) => a.student_id === student.key && a.date === filterDate
        );

        if (existingAttendance) {
          studentsToUpdate.push({
            id: existingAttendance.id,
            student_id: student.key,
            attendance_status: bulkStatus.toLowerCase(),
            date: filterDate,
          });
        }
      });

      if (studentsToUpdate.length > 0) {
        await updateBulkAttendance({
          attendance: studentsToUpdate,
        }).unwrap();
      }

      if (studentsToCreate.length > 0) {
        await createBulkAttendance({
          attendance: studentsToCreate.map((student) => ({
            student_id: student.key,
            attendance_status: bulkStatus.toLowerCase(),
            date: filterDate,
          })),
        }).unwrap();
      }

      showToast({
        title: "Success",
        text: `Attendance ${studentsToUpdate.length > 0 && studentsToCreate.length > 0
          ? "updated and marked"
          : studentsToUpdate.length > 0
            ? "updated"
            : "marked"
          } for ${selectedStudentKeys.length} students`,
        icon: "success",
        timer: 5000,
      });

      const selectedKeysSet = new Set(selectedStudentKeys);
      setStudents((prevStudents) =>
        prevStudents.map((student) => {
          if (selectedKeysSet.has(student.key)) {
            return {
              ...student,
              attendanceStatus:
                bulkStatus.charAt(0).toUpperCase() +
                bulkStatus.slice(1).toLowerCase(),
            };
          }
          return student;
        })
      );
      setAccumulatedStudents((prevStudents) =>
        prevStudents.map((student) => {
          if (selectedKeysSet.has(student.key)) {
            return {
              ...student,
              attendanceStatus:
                bulkStatus.charAt(0).toUpperCase() +
                bulkStatus.slice(1).toLowerCase(),
            };
          }
          return student;
        })
      );

      setSelectedStudentKeys([]);
    } catch (error) {
      console.error("Failed to update bulk attendance:", error);
      showToast({
        title: "Error",
        text: "Failed to update bulk attendance. Please try again.",
        icon: "error",
        timer: 5000,
      });
    }
  };

  const getStatusColor = useCallback((status: string) => {
    switch (status.toLowerCase()) {
      case "present":
        return "bg-success-transparent text-success";
      case "ncns":
        return "bg-warning-transparent text-warning";
      case "dropout":
        return "bg-info-transparent text-info";
      case "leave":
        return "bg-dark-transparent text-dark";
      case "absent":
        return "bg-danger-transparent text-danger";
      default:
        return "";
    }
  }, []);

  const getInitialAttendanceStatus = (status: string) => {
    return status ? status.toLowerCase() : "";
  };

  return {
    students,
    isLoading,
    selectedStudentKeys,
    bulkStatus,
    filterDate,
    currentDate,
    paginationData: {
      totalPages: studentData?.data?.totalPages || 1,
      pageSize: studentData?.data?.pageSize || 10,
      totalItems: studentData?.data?.totalItems || 0,
      currentPage: studentData?.data?.currentPage || 1,
    },
    setSelectedStudentKeys,
    setBulkStatus,
    setFilterDate,
    handleOptionChange,
    handleBulkStatusChange,
    isBulkUpdating,
    getStatusColor,
    getInitialAttendanceStatus,
    filters,
    tempFilters,
    setTempFilters,
    handleFilterChange: (filterName: string, value: any, courses?: any[]) => {
      const parsedValue = filterName === 'status' ? value?.value : (value?.value ? Number(value.value) : undefined);

      // Only update tempFilters — applied to the real query on Apply click
      setTempFilters((prev: any) => ({
        ...prev,
        [filterName]: parsedValue
      }));

      if (filterName === 'course_id' && courses) {
        const selectedCourse = courses.find((course: any) => course.id.toString() === value?.value);
        setSelectedCourseStages(selectedCourse?.courseStages || []);

        // Reset stage when course changes
        setTempFilters((prev: any) => ({
          ...prev,
          stage_id: undefined
        }));
      }
    },
    handleApplyFilters: () => {
      setFilters(tempFilters);
    },
    handleResetFilters: () => {
      const emptyFilters = {
        center_id: undefined,
        course_id: undefined,
        stage_id: undefined,
        status: undefined,
        session_id: undefined,
      };
      setFilters(emptyFilters);
      setTempFilters(emptyFilters);
      setSelectedCourseStages([]);
      setSortConfig({});
    },
    selectedCourseStages,
    sortConfig,
    handleSortChange: (sortBy: string, order: 'asc' | 'desc') => {
      const newOrder = sortConfig.sort_by === sortBy && sortConfig.sort_order === 'asc' ? 'desc' : 'asc';
      setSortConfig({
        sort_by: sortBy,
        sort_order: newOrder
      });
    },
    // New exports for lazy loading and pagination
    pageNumber,
    recordsPerPage,
    search,
    handlePageChange,
    handleSearchChange,
    isAllMode,
    setIsAllMode,
    accumulatedStudents,
    lazyHasMore,
    isLazyLoading: isFetching && pageRequestedRef.current,
    loadNextLazyPage,
  };
};
